import {environment} from '../../environments/environment';
import {
  AppConfig,
  AppConfiguration,
  GeneralState,
  State,
  StateInterface
} from '../interfaces/biozoom-api/app-configuration';
import {ScannerStatus} from '../interfaces/scanner-api/scanner-status';
import {of} from 'rxjs';
import {Scanner} from '../interfaces/scanner-api/scanner';

const _ = require('lodash');

// language codes, as utilized by DeepL
// this app as well as the connected services all use these tags as well
export const languageCodes = {
  BG: 'Български',
  CS: 'Česky',
  DA: 'Dansk',
  DE: 'Deutsch',
  EL: 'Ελληνική',
  'EN-GB': 'English (UK)',
  'EN-US': 'English (US)',
  ES: 'Español',
  ET: 'Eesti',
  FI: 'Suomalainen',
  FR: 'Français',
  HU: 'Magyar',
  ID: 'Bahasa Indonesia',
  IT: 'Italiano',
  JA: '日本語',
  KO: '한국어',
  LT: 'Lietuvių kalba',
  LV: 'Latviešu',
  NB: 'Norsk (bokmål)',
  NL: 'Nederlands',
  PL: 'Polski',
  'PT-BR': 'Português (brasileiro)',
  'PT-PR': 'Português',
  RO: 'Românesc',
  RU: 'Русский',
  SK: 'Slovenská',
  SL: 'Slovaška',
  SV: 'Svenska',
  TR: 'Türkçe',
  UK: 'Українська',
  ZH: '中文（简体）',
  'ZH-TW': '中文（繁體）',
  MN: 'Монгол',
  VI: 'Tiếng Việt',
  TH: 'ไทย',
};

export const SCANNER_APMODE_URL = 'http://192.168.4.1';
/**
 * fetch a translated string by accessing a value from a translation-dictionary
 * multiple stores have the same keys, and the values are the actual text in different languages
 * @param text key of the translation dictionary
 * @param lanStore language store containing the translated strings
 */
export const translateString = (text: string, lanStore: Record<string, string>) => {
  const lanCodes = getLanguageCodes();
  if (lanCodes[text]) {
    return lanCodes[text];
  }
  if (!lanStore) {
    return text;
  }
  if (lanStore[text]) {
    return lanStore[text];
  }
  const defaultDesign = require('../../../resources/designs/default.json');
  const lan = localStorage.getItem('SELECTED_LANGUAGE') ?? environment.config.defaultLang;
  if (defaultDesign.language?.[lan]?.[text]) {
    return defaultDesign.language[lan][text];
  }
  return getDefaultLanguageStore()[text] ?? text;
};

export const getDefaultLanguageStore = (): Record<string, string> => {
  return environment.appDesignLanguages?.[environment.config.defaultLang] ?? {};
};

export const versionUpdate = (storeVersion: string, appVersion: string) => {
  if (storeVersion) {
    return (storeVersion[0] < appVersion[0]) ||
      storeVersion[0] === appVersion[0] && storeVersion[1] < appVersion[1];
  }
  return true;
};

export const updateESPNeeded = (serial: string, currentVersion: string, config: AppConfiguration) => {
  if (serial[1] === 'W') {
    return false;
  }
  const versionParsed = currentVersion.split('.').map((p) => Number(p.padStart(2, '0')));
  const major = Number(currentVersion.split('.')[0]);
  if (major < 10) {
    return false;
  }

  const targetVersion = (major >= 20 ? config.firmware_20_version : config.firmware_10_version);
  const targetVersionParsed = targetVersion.split('.').map((p) => Number(p.padStart(2, '0')));

  let shouldUpdate = false;
  for (let i = 0; i < 3; i++) {
    if (versionParsed[i] === targetVersionParsed[i]) {
    } else if (versionParsed[i] > targetVersionParsed[i]) {
      shouldUpdate = false;
      break;
    } else {
      shouldUpdate = true;
      break;
    }
  }
  return shouldUpdate;
};

export const updateNUVNeeded = (serial: string, currentVersion: string, config: AppConfiguration) => {
  if (serial.slice(0, 2) !== '2N') {
    return false;
  }
  const versionParsed = currentVersion.split('.').map((p) => Number(p.padStart(2, '0')));

  const targetVersion = config.firmware_nuvoton_version;
  const targetVersionParsed = targetVersion.split('.').map((p) => Number(p.padStart(2, '0')));

  let shouldUpdate = false;
  for (let i = 0; i < 3; i++) {
    if (versionParsed[i] === targetVersionParsed[i]) {
    } else if (versionParsed[i] > targetVersionParsed[i]) {
      shouldUpdate = false;
      break;
    } else {
      shouldUpdate = true;
      break;
    }
  }
  return shouldUpdate;
};

export const updateInfNeeded = (scannerVersion: number, sensorVersion: number, wifiFirmware: string, config: AppConfiguration) => {
  if (scannerVersion < 2 || !wifiFirmware || !sensorVersion) {
    return false;
  }

  const major = Number(wifiFirmware.split('.')[0]);
  const minor = Number(wifiFirmware.split('.')[1]);
  if (minor < 3 && (major <= 10 || major === 20)) {
    return false;
  }
  if (major < 20 && (sensorVersion ?? 999999) >= 602) {
    return false;
  }

  const targetVersion = config.sensor_version;
  if ((targetVersion ?? 0) > (sensorVersion ?? 999999)) {
    return true;
  }
  return false;
};

export const checkScannerBattery = (scannerStatus: ScannerStatus,
                                    batteryThreshold: number) => {
  if (!scannerStatus?.battery_voltage || scannerStatus?.battery_voltage === 0) {
    return of(true);
  }
  // if the scannerStatus could not have been fetched,
  // undefined<num is always false, meaning this function will return true regardless
  return !((scannerStatus?.battery_voltage ?? (batteryThreshold)) < batteryThreshold);
};

export const isVersionEqualOrNewer = (status: ScannerStatus | {}, config: AppConfiguration, downgrade = false): string => {
  if (Object.keys(status).length === 0) {
    return 'unknown';
  }
  if ('wifi_firmware' in status && status.wifi_firmware) {
    const version = status.wifi_firmware.split('.');
    if (version.length === 3) {
      let minVersion;
      let filename;
      switch (version[0]) {
        case '1':
          minVersion = config.firmware_hw1_version.split('.');
          filename = config.firmware_hw1_filename;
          break;
        case '2':
          minVersion = config.firmware_hw2_version.split('.');
          filename = config.firmware_hw2_filename;
          break;
        case '3':
          minVersion = config.firmware_hw3_version.split('.');
          filename = config.firmware_hw3_filename;
          break;
        case '4':
          minVersion = config.firmware_hw4_version.split('.');
          filename = config.firmware_hw4_filename;
          break;
        case '10':
          minVersion = config.firmware_10_version.split('.');
          filename = config.firmware_10_filename;
          break;
        case '20':
          minVersion = config.firmware_20_version.split('.');
          filename = config.firmware_20_filename;
          break;
        default:
          minVersion = [0, 0, 0]; // force OK for unknown major version
          break;
      }

      if (
        parseInt(minVersion[1], 10) > parseInt(version[1], 10) ||
        (parseInt(minVersion[1], 10) === parseInt(version[1], 10) &&
          parseInt(minVersion[2], 10) > parseInt(version[2], 10))
      ) {
        return filename;
      } else {
        if (downgrade) {
          return filename;
        }
        return 'ok';
      }
    }
  } else {
    return 'unknown';
  }
};

export const getScanner = async (state: StateInterface, serialUSB = ''): Promise<Scanner> => {
  let scanner: Scanner;
  const serial = '1E19A8ED89BF713C2E9C';
  let host = 'biozoom_dummy';
  if (state.DEMO_MODE) {
    return {
      online: true,
      host,
      serial,
      ip: '192.168.4.1',
      product: 'DUMMY'
    };
  }
  if (state.USB_MODE) {
    if (serialUSB === '') {
      return;
    }
    host = 'biozoom_usb';
    scanner = {
      online: true,
      host,
      serial: serialUSB,
      ip: '192.168.4.1',
      product: 'USB'
    };
  }

  if (!scanner) {
    const scannerJson = state.SCANNER_KEY;
    if (scannerJson && scannerJson !== '') {
      scanner = JSON.parse(scannerJson);
    }
  }

  return scanner;
};

/**
 * retrieve a (nested) value from an object
 * if it does not exist, return undefined
 * @param key (nested) key, separated with periods
 * @param obj object to retrieve the value from
 */
export const getKeyVal = (key: string, obj: Record<string, any>) => {
  const keySplit = key.split('.');
  let val: any = obj;
  for (const k of keySplit) {
    val = val[k];
    if (!val) {
      break;
    }
  }
  return val;
};

export const setKeyVal = (obj: Record<string, any>, key: string, newVal: any) => {
  const keySplit = key.split('.');
  let val = obj;
  for (const k of keySplit.slice(0, -1)) {
    const v = val[k];
    if (!v) {
      val[k] = {};
      // break;
    }
    val = val[k];
  }
  val[keySplit.slice(-1)[0]] = newVal;
  return val;
};

/**
 * get the url for the api server that should be used
 * if the currently loaded config specifies a server, use that one
 * otherwise, use the default one store in the environment file
 */
export const getApiUrl = (cycle = false) => {
  const configURLs = environment.appConfigUrl;
  const numURLs = configURLs.length;
  let currentIndex = Number(document.cookie.split('; ').filter((c) => c.includes('apiUrlIndex'))[0]?.split('=')[1]);
  if (!currentIndex) {
    currentIndex = 0;
  }
  if (cycle) {
    currentIndex = (currentIndex + 1) % numURLs;
    document.cookie = `apiUrlIndex=${currentIndex}`;
  }
  return configURLs[currentIndex];
  //
  // return document.cookie.split('; ').filter((c) => c.includes('apiUrl'))[0]?.split('=')[1] ?? environment.appConfigUrl;
};

/**
 * create a dictionary mapping language tag to the language's full name in its native language
 * @param rev if true, map language name to language tag instead
 */
export const getLanguageCodes = (rev = false) => {
  if (rev) {
    const codes: Record<string, string> = {};
    Object.entries(languageCodes).forEach(([key, val]) => {
      codes[val] = key;
    });
    return codes;
  }
  return languageCodes;
};

/**
 * replace a string's placeholders with values from the value store
 * placeholders have this form:
 * ${group.key}
 * @param urlIn raw url, with placeholder that are to be filled
 * @param state value store, use  to retrieve values from
 */
export const fillURL = (urlIn: string, state: GeneralState) => {
  let url = urlIn;
  while (url.includes('${')) {
    const index0 = url.indexOf('${');
    const index1 = url.indexOf('}');
    if (index1 === -1) {
      break;
    }
    const searchStr = url.slice(index0 + 2, index1);
    const searchStrSplit = searchStr.split('.');
    let recState: any = state;
    for (const searchStrIt of searchStrSplit) {
      recState = recState[searchStrIt];
      if (!recState) {
        break;
      }
    }
    if (typeof recState === 'object') {
      recState = JSON.stringify(recState);
    }
    recState = encodeURIComponent(recState ?? '');
    url = `${url.slice(0, index0)}${recState}${url.slice(index1 + 1)}`;
  }
  return url;
};

export const fillString = (stringIn: string, state: GeneralState) => {
  let stringOut = stringIn;
  while (stringOut.includes('${')) {
    const index0 = stringOut.indexOf('${');
    const index1 = stringOut.indexOf('}');
    if (index1 === -1) {
      break;
    }
    const searchStr = stringOut.slice(index0 + 2, index1);
    const searchStrSplit = searchStr.split('.');
    let recState: any = state;
    for (const searchStrIt of searchStrSplit) {
      recState = recState[searchStrIt];
      if (!recState) {
        break;
      }
    }
    // recState = encodeURIComponent(recState ?? '');
    stringOut = `${stringOut.slice(0, index0)}${recState}${stringOut.slice(index1 + 1)}`;
  }
  return stringOut;
};

export const getPostURL = (page: AppConfig): string | undefined => {
  const callFunc = page.settings.callFunction;
  if (!callFunc) {
    return;
  }
  let url: any;
  if (callFunc === 'postRequest') {
    url = page.settings.postURL;
  // uploadDataAOX and uploadDataHeart are legacy config functions kept for compatibility.
  } else if (['uploadDataAOX', 'uploadDataHeart', 'postRequest'].includes(callFunc)) {
    url = 'https://biozoom.net/';
  } else {
    const elements = Object.values(page.design.elements);
    const iFrame = elements.filter((el) => el.elementType === 'iFramePost')[0];
    if (iFrame) {
      url = iFrame.design;
    }
  }

  if (url) {
    const re = /^(.*?)\/\/(.*?)\//;
    url = re.exec(url);
    if (!url) {
      return;
    }
    return url[0];
  }

  return;
};

export const copyObject = (data: object) => {
  return _.cloneDeep(data);
};

export const parseStatus = (inputStr: string, setState: ((key: State, val: any) => void)): [[string, number], boolean] => {
  let ser = '';
  let verStr = 0;
  let err = false;
  try {
    const statusJson = JSON.parse(inputStr);
    const serial = statusJson['serial'];
    if (serial) {
      setState(State.SCANNER_STATUS, statusJson);
      ser = serial;
      if (Object.keys(statusJson).includes('command')) {
        verStr = 2;
      } else {
        verStr = 1;
      }
      if (Object.keys(statusJson).includes('type') && statusJson.type === 'error') {
        err = true;
      }
    }
  } catch (e) {
  }
  return [[ser, Number(verStr)], err];
};

/**
 * Represents the software version of the scanner.
 */
export type scannerSoftwareVersion = 'Deprecated' | 'Matrix' | 'R5|R6|R8' | 'R10' | 'R10-LB'| 'R10-LB-USB' | scannerSoftwareVersionR5toR8;

/**
 * Maps scanner serial number prefixes to their corresponding software versions.
 */
export const scannerSerialNumberScannerSoftwareMap: Record<string, scannerSoftwareVersion> = {
  '1X': 'Deprecated',
  '4W': 'Matrix',
  '5W': 'Matrix',
  '1E19': 'R5|R6|R8',
  '1E24': 'R10',
  '2E24': 'R10-LB',
  '2N24' : 'R10-LB-USB',
};

/**
 * Returns the scanner software version based on the provided serial number.
 * @param scannerSerialNumber The serial number of the scanner.
 * @returns The software version of the scanner.
 */
export const getScannerSoftwareVersionBySerialNumber =
    (scannerSerialNumber: string): scannerSoftwareVersion => {
      let prefix = scannerSerialNumber.substring(0, 4);
      if (prefix in scannerSerialNumberScannerSoftwareMap) {
        return scannerSerialNumberScannerSoftwareMap[prefix];
      }
      else{
        prefix = scannerSerialNumber.substring(0, 2);
        if (prefix in scannerSerialNumberScannerSoftwareMap) {
          return scannerSerialNumberScannerSoftwareMap[prefix];
        }
      }
      return 'unhandledCase';
    };

/**
 * Represents the software version of the scanner in the R5 to R8 range.
 */
export type scannerSoftwareVersionR5toR8 = 'R5' | 'R6' | 'R6-added-platine' | 'R8' | 'unhandledCase';

/**
 * Maps scanner wifi firmware prefixes to their corresponding software versions.
 */
export const scannerWifiFirmwareMap: Record<string, scannerSoftwareVersionR5toR8> = {
  1: 'R5',
  2: 'R6',
  3: 'R6-added-platine',
  4: 'R8',
};

/**
 * Returns the scanner software version based on the provided wifi firmware.
 * @param scannerWifiFirmware The wifi firmware of the scanner.
 * @returns The software version of the scanner.
 */
export const getScannerSoftwareVersionByWifiFirmware =
    (scannerWifiFirmware: string): scannerSoftwareVersionR5toR8 => {
      const prefix = scannerWifiFirmware.substring(0, 1);
      if (prefix in scannerWifiFirmwareMap) {
        return scannerWifiFirmwareMap[prefix];
      }
      return 'unhandledCase';
    };


export type ScannerSCNModel = 'MRSRRS_SCN05' | 'MRSRRS_SCN06' | 'unidentified';

export const getScannerSCNModelFromSerialNumber = (serial: string): ScannerSCNModel => {
  if (serial.startsWith('1E') || serial.startsWith('1N')) {
    return 'MRSRRS_SCN05';
  } else if (serial.startsWith('2E') || serial.startsWith('2N')) {
    return 'MRSRRS_SCN06';
  } else {
    return 'unidentified';
  }
};

/**
 * Flattens a nested object or array into a single-level object with dot-notated keys.
 * @param obj The object or array to flatten.
 * @param prefix The prefix to prepend to each key (used internally for recursion).
 * @param result The accumulated result object (used internally for recursion).
 * @returns A flattened version of the input object.
 */
export const flattenObject = (obj: unknown, prefix = '', result: Record<string, unknown> = {}): Record<string, unknown> => {
  // ARRAY
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const newKey = prefix ? `${prefix}.${index}` : String(index);
      flattenObject(item, newKey, result);
    });
    return result;
  }

  // OBJECT
  if (obj !== null && typeof obj === 'object') {
    Object.keys(obj as Record<string, unknown>).forEach(key => {
      const value = (obj as Record<string, unknown>)[key];
      const newKey = prefix ? `${prefix}.${key}` : key;
      flattenObject(value, newKey, result);
    });
    return result;
  }

  // PRIMITIVE
  result[prefix] = obj;
  return result;
};

/**
 * Rounds a number to two decimal places.
 * @param value The number to round.
 */
export const roundValueToTwoDigests = (value: number): number => {
  return Math.round(value * 100) / 100;
};
